import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useParams, useNavigate, useLocation, Link } from "react-router-dom";
import { computeEpisodeDisplay } from "../../utils/episodeNumbering";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  createDrama,
  updateDrama,
  getDramaWithEpisodes,
  addEpisodeToDrama,
  removeEpisodeFromDrama,
  getDramaCoverUrl,
  getDramaFavoritesCount,
  getDramaTotalLikes,
} from "@/services/firebase/drama";
import {
  createVideo,
  updateVideo,
  deleteVideo,
} from "@/services/firebase/video";
import { getTags, getActors, getCompanies, getProducts } from "@/services/firebase/references";
import { Drama, VideoMeta } from "@/types/video";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import ImageUpload from "@/components/common/ImageUpload";
import MultiSelect, { MultiSelectOption } from "@/components/common/MultiSelect";
import ArtistCastPicker, { ArtistCastOption } from "@/components/drama/ArtistCastPicker";
import { artistAdminList, artistHubEnabled } from "@/services/firebase/artistHub";
import type { ArtistAdminRow } from "@/lib/artistAdmin";
import VideoFormDialog from "@/components/video/VideoFormDialog";
import VideoPreviewDialog from "@/components/video/VideoPreviewDialog";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import PublishScheduleDialog from "@/components/drama/PublishScheduleDialog";
import { formatTaipeiHour } from "@/utils/publishSchedule";
import ActionMenu from "@/components/common/ActionMenu";
import VideoUploadSection from "@/components/video/VideoUploadSection";
import DisplayMetricsCard from "@/components/video/DisplayMetricsCard";
import { useDisplayAdjustments } from "@/hooks/useDisplayAdjustments";
import { dramaAdjustment, videoLikeAdjustment } from "@/services/firebase/displayMetric";
import { uploadFile, getFileDownloadURL } from "@/services/firebase/storage";
import { validateImageFile } from "@/utils/storageUtils";
import { exportToCSV } from "@/utils/csvExport";
import { getDramaEpisodeStats } from "@/services/firebase/transaction";

import {
  AdminPage,
  AdminPageHeader,
  AdminLoadingSkeleton,
  AdminErrorAlert,
  AdminStatsCard,
  AdminStatusBadge,
} from "@/components/admin-ui";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  ArrowLeft,
  Save,
  Loader2,
  Plus,
  Trash2,
  X,
  Eye,
  Bookmark,
  Heart,
  Coins,
  Download,
  ImageIcon,
  Check,
  Copy,
  Play,
  Upload,
  Ban,
  AlertTriangle,
  Clock,
  CalendarClock,
} from "lucide-react";

const dramaSchema = z.object({
  drama_id: z.string().optional(),
  drama_title: z.string().min(1, "劇集標題為必填"),
  drama_title_en: z.string().optional(),
  isReleased: z.boolean(),
  new_release: z.boolean(),
  ad_unlock_enabled: z.boolean(),
  coin_unlock_enabled: z.boolean(),
  drama_video_orientation: z.number().min(0).max(1),
  drama_cover_url: z.string().optional(),
  search_keywords: z.array(z.string()).optional(),
  tags: z.array(z.string()).optional(),
  company: z.string().optional(),
  actors: z.array(z.string()).optional(),
  related_products: z.array(z.string()).optional(),
});

type DramaFormData = z.infer<typeof dramaSchema>;

export default function DramaEditPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  // 路由 /dramas/new 沒有 :id 參數（useParams 回 undefined），需用 pathname 補判斷
  const isNew = id === "new" || location.pathname.endsWith("/dramas/new");

  // 載入劇集數據（包含影集）
  const { data: dramaData, isLoading } = useQuery({
    queryKey: ["drama", id, "with-episodes"],
    queryFn: () => getDramaWithEpisodes(id!),
    enabled: !isNew && !!id,
  });

  const drama = dramaData?.drama;
  const videos = dramaData?.videos || [];

  // 集數欄顯示：依集型自動編號（正片序跳過 SP/預告），key = vid。
  // 在 order 排序後計算 rank，再以 vid 對應回各列（不依賴渲染列序）。
  const episodeDisplayByVid = useMemo(() => {
    const sorted = [...videos].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    const infos = computeEpisodeDisplay(sorted);
    const map: Record<string, string> = {};
    sorted.forEach((v, i) => {
      if (v.vid) map[v.vid] = infos[i].display;
    });
    return map;
  }, [videos]);

  // 前 N 集免費（按 order 排序，連續計算從首集到第一個付費集之前的數量）
  const currentFreeEpisodeCount = useMemo(() => {
    const sorted = [...videos].sort((a, b) => (a.order ?? 0) - (b.order ?? 0));
    let count = 0;
    for (const v of sorted) {
      if (v.vip) break;
      count += 1;
    }
    return count;
  }, [videos]);

  // 載入統計數據
  const { data: favoritesCount = 0, isLoading: favoritesLoading } = useQuery({
    queryKey: ["drama", id, "favorites-count"],
    queryFn: () => getDramaFavoritesCount(id!),
    enabled: !isNew && !!id,
    staleTime: 30 * 1000, // 30 秒，因為 trigger 會自動更新
  });

  const { data: totalLikes = 0, isLoading: likesLoading } = useQuery({
    queryKey: ["drama", id, "total-likes"],
    queryFn: () => getDramaTotalLikes(id!),
    enabled: !isNew && !!id,
    staleTime: 30 * 1000, // 30 秒
  });

  // 後台統計一律顯示真實值：欄位值 − App 顯示數據調整量
  const { data: displayAdjustments } = useDisplayAdjustments();
  const realVideoLike = (video: VideoMeta & { id?: string }) =>
    (video.like || 0) - videoLikeAdjustment(displayAdjustments, id, video.id);
  const realPlayTimes =
    (drama?.drama_play_times || 0) - dramaAdjustment(displayAdjustments, id, "drama_play_times");
  const realFavoritesCount = favoritesCount - dramaAdjustment(displayAdjustments, id, "favorites_count");
  const realTotalLikes =
    totalLikes -
    Object.values(displayAdjustments?.get(id ?? "")?.videos ?? {}).reduce(
      (sum, v) => sum + Number(v.likeCount || 0),
      0
    );

  const { data: episodeStats, isLoading: episodeStatsLoading } = useQuery({
    queryKey: ["drama", id, "episode-stats"],
    queryFn: () => getDramaEpisodeStats(id!),
    enabled: !isNew && !!id,
    staleTime: 60 * 1000, // 1 分鐘
  });

  // 載入參考數據（使用較長的 staleTime，因為這些數據不經常變化）
  const { data: tags = [], isLoading: tagsLoading } = useQuery({
    queryKey: ["tags"],
    queryFn: () => getTags(),
    staleTime: 5 * 60 * 1000, // 5 分鐘
  });

  const { data: actors = [], isLoading: actorsLoading } = useQuery({
    queryKey: ["actors"],
    queryFn: () => getActors(),
    staleTime: 5 * 60 * 1000, // 5 分鐘
  });

  const { data: companies = [], isLoading: companiesLoading } = useQuery({
    queryKey: ["companies"],
    queryFn: () => getCompanies(),
    staleTime: 5 * 60 * 1000, // 5 分鐘
  });

  const { data: products = [], isLoading: productsLoading } = useQuery({
    queryKey: ["products"],
    queryFn: () => getProducts(),
    staleTime: 5 * 60 * 1000, // 5 分鐘
  });

  const referencesLoading = tagsLoading || actorsLoading || companiesLoading || productsLoading;

  // 轉換參考數據為選項格式（使用 useMemo 優化性能）
  const tagOptions: MultiSelectOption[] = React.useMemo(
    () =>
      tags.map((tag) => ({
        id: tag.id,
        label: tag.tw || tag.zh || tag.en || tag.id,
      })),
    [tags]
  );

  const actorOptions: ArtistCastOption[] = React.useMemo(
    () =>
      actors.map((actor) => ({
        id: actor.id,
        label: actor.name || actor.id,
        photoURL: (actor as { photoURL?: string }).photoURL ?? actor.photo_url,
      })),
    [actors]
  );

  // 藝人專頁狀態（只在 artistHubEnabled 環境；失敗或關閉時不顯示狀態標記）
  const { data: artistStatus } = useQuery({
    queryKey: ["artist-admin", "all"],
    queryFn: () => artistAdminList(),
    enabled: artistHubEnabled,
    staleTime: 60 * 1000,
    retry: false,
  });
  const artistStatusById = React.useMemo(
    () => (artistStatus ? new Map<string, ArtistAdminRow>(artistStatus.items.map((r) => [r.artistId, r])) : undefined),
    [artistStatus]
  );

  const companyOptions: MultiSelectOption[] = React.useMemo(
    () =>
      companies.map((company) => ({
        id: company.id,
        label: company.company_name || company.id,
      })),
    [companies]
  );

  const productOptions: MultiSelectOption[] = React.useMemo(
    () =>
      products.map((product) => ({
        id: product.id,
        label: product.name || product.id,
      })),
    [products]
  );

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
    setValue,
    watch,
    reset,
    getValues,
  } = useForm<DramaFormData>({
    resolver: zodResolver(dramaSchema),
    defaultValues: {
      drama_id: "",
      drama_title: "",
      drama_title_en: "",
      isReleased: false,
      new_release: false,
      ad_unlock_enabled: false,
      coin_unlock_enabled: true,
      drama_video_orientation: 0,
      drama_cover_url: "",
      search_keywords: [],
      tags: [],
      company: "",
      actors: [],
      related_products: [],
    },
  });

  // 監聽表單值變化
  const watchTags = watch("tags") || [];
  const watchActors = watch("actors") || [];
  const watchRelatedProducts = watch("related_products") || [];

  // 封面圖顯示狀態（提前宣告，因為 M6 自動保存 effect 與初始化 useEffect 都會用到）
  const [coverUrlDisplay, setCoverUrlDisplay] = useState<string>("");

  // ─── M6: 表單草稿自動保存（localStorage） ───────────────────────────
  // key 用 id（'new' 為新增）；草稿 7 天後過期
  const draftKey = React.useMemo(() => `drama-edit-draft:${id ?? "new"}`, [id]);
  const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;
  // 還原提示（mount 後若發現有效草稿就顯示橫幅）
  const [draftRecovery, setDraftRecovery] = useState<{
    data: DramaFormData;
    coverUrlDisplay?: string;
    savedAt: number;
  } | null>(null);
  const [draftLoaded, setDraftLoaded] = useState(false); // 防止初始 reset 後立即被自動保存覆寫
  const [saveError, setSaveError] = useState<string | null>(null);

  // 初始化表單數據
  useEffect(() => {
    if (drama) {
      const formData: DramaFormData = {
        drama_id: drama.drama_id || "",
        drama_title: drama.drama_title || "",
        drama_title_en: drama.drama_title_en || "",
        isReleased: drama.isReleased || false,
        new_release: drama.new_release || false,
        ad_unlock_enabled: drama.ad_unlock_enabled || false,
        // 未設定視為允許，與 App / 後端的 !== false 判斷一致
        coin_unlock_enabled: drama.coin_unlock_enabled !== false,
        drama_video_orientation: drama.drama_video_orientation ?? 0,
        drama_cover_url: drama.drama_cover_url || "",
        search_keywords: drama.search_keywords || [],
        tags: drama.tags || [],
        company: drama.company || "",
        actors: drama.actors || [],
        related_products: drama.related_products || [],
      };
      // 使用 reset 來設置初始值，這樣不會觸發 isDirty
      reset(formData);

      // 處理封面 URL 顯示(同步)
      setCoverUrlDisplay(getDramaCoverUrl(drama.drama_cover_url));
      setDraftLoaded(true);
    } else if (isNew) {
      // 新劇集：生成 drama_id
      const formData: DramaFormData = {
        drama_id: `drama_${Date.now()}`,
        drama_title: "",
        drama_title_en: "",
        isReleased: false,
        new_release: false,
        ad_unlock_enabled: false,
        coin_unlock_enabled: true,
        drama_video_orientation: 0,
        drama_cover_url: "",
        search_keywords: [],
        tags: [],
        company: "",
        actors: [],
        related_products: [],
      };
      reset(formData);
      setCoverUrlDisplay("");
      setDraftLoaded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [drama, isNew, reset]);

  // M6: mount 後檢查是否有未提交草稿（在 reset 完成後執行）
  useEffect(() => {
    if (!draftLoaded) return;
    try {
      const raw = localStorage.getItem(draftKey);
      if (!raw) return;
      const parsed = JSON.parse(raw) as {
        data: DramaFormData;
        coverUrlDisplay?: string;
        savedAt: number;
      };
      if (
        !parsed?.savedAt ||
        Date.now() - parsed.savedAt > DRAFT_MAX_AGE_MS
      ) {
        localStorage.removeItem(draftKey);
        return;
      }
      // 比對與目前表單值是否相同（相同則無需提示）
      const currentJson = JSON.stringify(getValues());
      const draftJson = JSON.stringify(parsed.data);
      if (currentJson === draftJson) {
        // 內容一致，視為已套用，清除避免下次仍提示
        localStorage.removeItem(draftKey);
        return;
      }
      setDraftRecovery(parsed);
    } catch {
      localStorage.removeItem(draftKey);
    }
    // 只在 draftLoaded 從 false → true 時跑一次
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftLoaded, draftKey]);

  // M6: 30 秒自動保存（草稿未還原前不保存，避免覆寫掉 cache）
  useEffect(() => {
    if (!draftLoaded) return;
    const interval = setInterval(() => {
      try {
        const snapshot = getValues();
        localStorage.setItem(
          draftKey,
          JSON.stringify({
            data: snapshot,
            coverUrlDisplay,
            savedAt: Date.now(),
          })
        );
      } catch {
        // localStorage 可能滿了或被禁用，忽略
      }
    }, 30000);
    return () => clearInterval(interval);
  }, [draftLoaded, draftKey, getValues, coverUrlDisplay]);

  // M6: 還原 / 忽略草稿
  const handleRestoreDraft = () => {
    if (!draftRecovery) return;
    // 草稿尚未寫入 Firestore，須與伺服器初始值比較，才能啟用儲存。
    reset(draftRecovery.data, { keepDefaultValues: true });
    // 草稿可能是加集數前存的，方向已鎖定不可被草稿覆寫：強制還原為 Firestore 已存值
    if (!isNew && videos.length > 0) {
      setValue("drama_video_orientation", drama?.drama_video_orientation ?? 0, { shouldDirty: true });
    }
    if (draftRecovery.coverUrlDisplay) {
      setCoverUrlDisplay(draftRecovery.coverUrlDisplay);
    }
    setDraftRecovery(null);
  };
  const handleDiscardDraft = () => {
    try {
      localStorage.removeItem(draftKey);
    } catch {}
    setDraftRecovery(null);
  };

  const mutation = useMutation({
    mutationFn: async (data: DramaFormData) => {
      if (isNew) {
        return await createDrama({
          ...data,
          drama_id: data.drama_id || `drama_${Date.now()}`,
          drama_cover_url: data.drama_cover_url || "",
          drama_length: 0,
          drama_play_times: 0,
        });
      } else {
        // 上下架只走上方按鈕／預約上架；表單儲存不帶 isReleased，避免舊頁面把已上架的劇改回未上架
        const { isReleased: _isReleased, ...changes } = data;
        await updateDrama(id!, changes);
        return id;
      }
    },
    onSuccess: (savedId) => {
      queryClient.invalidateQueries({ queryKey: ["dramas"] });
      queryClient.invalidateQueries({ queryKey: ["drama", id] });
      queryClient.invalidateQueries({ queryKey: ["drama-with-episodes", id] });
      queryClient.invalidateQueries({ queryKey: ["dramas-by-company"] });
      queryClient.invalidateQueries({ queryKey: ["dramas-by-actor"] });
      // M6: 儲存成功，清除草稿
      try {
        localStorage.removeItem(draftKey);
      } catch {}
      // 儲存後跳轉到詳情頁(若是新增則用 createDrama 回傳的 ID)
      navigate(`/dramas/${savedId || id}`);
    },
    onError: (error) => {
      const code = (error as { code?: string }).code?.replace(/^firestore\//, "");
      setSaveError(code === "permission-denied"
        ? "儲存失敗：目前帳號沒有修改劇集的權限，請聯絡管理員確認。你的變更仍保留在此頁。"
        : code === "unauthenticated"
          ? "儲存失敗：登入已失效，請重新登入後再試。你的變更仍保留在此頁。"
          : code === "unavailable" || code === "deadline-exceeded"
            ? "儲存失敗，請確認網路連線後重試。你的變更仍保留在此頁，尚未寫入後台。"
            : "儲存失敗，請重試；若持續發生，請聯絡管理員。你的變更仍保留在此頁，尚未寫入後台。");
    },
  });

  const onSubmit = (data: DramaFormData) => {
    setSaveError(null);
    // 新增時若 drama_id 為空（例如被空草稿還原覆寫），即時補上
    const payload: DramaFormData = {
      ...data,
      drama_id:
        data.drama_id && data.drama_id.trim() !== ""
          ? data.drama_id
          : `drama_${Date.now()}`,
    };
    mutation.mutate(payload);
  };

  // 處理搜尋關鍵字
  const [keywordInput, setKeywordInput] = useState("");
  const [showVideoForm, setShowVideoForm] = useState(false);
  const [editingVideo, setEditingVideo] = useState<(VideoMeta & { id: string }) | null>(null);
  // M4: 集數預覽 dialog
  const [previewVideo, setPreviewVideo] = useState<(VideoMeta & { id: string }) | null>(null);
  const [coverUploading, setCoverUploading] = useState(false);
  const [coverUploadError, setCoverUploadError] = useState<string | null>(null);
  const coverFileInputRef = React.useRef<HTMLInputElement>(null);

  // 上下架相關狀態
  const [releaseConfirm, setReleaseConfirm] = useState<{
    isOpen: boolean;
    action: "release" | "unrelease";
  }>({ isOpen: false, action: "release" });
  const [validationErrors, setValidationErrors] = useState<string[]>([]);
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [releaseError, setReleaseError] = useState<string | null>(null);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleError, setScheduleError] = useState<string | null>(null);
  const [copiedVid, setCopiedVid] = useState<string | null>(null);

  // 處理 ESC 鍵關閉錯誤視窗
  useEffect(() => {
    if (!showValidationErrors) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setShowValidationErrors(false);
        setValidationErrors([]);
      }
    };

    document.addEventListener("keydown", handleEscape);
    return () => {
      document.removeEventListener("keydown", handleEscape);
    };
  }, [showValidationErrors]);

  const handleAddKeyword = () => {
    if (keywordInput.trim()) {
      const currentKeywords = watch("search_keywords") || [];
      setValue("search_keywords", [...currentKeywords, keywordInput.trim()], { shouldDirty: true });
      setKeywordInput("");
    }
  };

  const handleRemoveKeyword = (index: number) => {
    const currentKeywords = watch("search_keywords") || [];
    setValue("search_keywords", currentKeywords.filter((_, i) => i !== index), { shouldDirty: true });
  };

  // 處理封面圖上傳
  const handleCoverImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // 驗證文件
    const validation = validateImageFile(file, 5);
    if (!validation.valid) {
      setCoverUploadError(validation.error || "圖片驗證失敗");
      return;
    }

    // 封面比例一律 2:3（±10% 容差，僅警告不阻擋）——不分劇的方向。
    // App 讀 drama_cover_url 的地方（grid cell / 排行 / 輪播 / 搜尋 / 觀看紀錄）
    // 全是直式框，橫式劇上傳 16:9 只會在框裡剩 37.5% 寬、標題被裁爛。
    try {
      const dims = await new Promise<{ w: number; h: number }>((resolve, reject) => {
        const img = new Image();
        const objectUrl = URL.createObjectURL(file);
        img.onload = () => {
          URL.revokeObjectURL(objectUrl); // 釋放 blob URL，避免記憶體洩漏
          resolve({ w: img.naturalWidth, h: img.naturalHeight });
        };
        img.onerror = (e) => {
          URL.revokeObjectURL(objectUrl);
          reject(e);
        };
        img.src = objectUrl;
      });
      const expected = 2 / 3;
      const actual = dims.w / dims.h;
      if (Math.abs(actual - expected) / expected > 0.1) {
        alert(
          `⚠️ 封面比例 ${dims.w}×${dims.h} 不是 2:3，App 卡片會裁切或留黑邊。\n橫式劇也請上傳 2:3 直式主視覺（卡片一律是直式框）。\n（仍會照常上傳，可稍後更換）`,
        );
      }
    } catch {
      // 讀不到尺寸不阻擋上傳
    }

    setCoverUploadError(null);
    setCoverUploading(true);

    try {
      // 創建預覽
      const reader = new FileReader();
      reader.onloadend = () => {
        setCoverUrlDisplay(reader.result as string);
      };
      reader.readAsDataURL(file);

      // 上傳文件
      const fileName = `${Date.now()}_${file.name}`;
      const path = `drama_covers/${fileName}`;
      await uploadFile(path, file);

      // 獲取下載 URL 用於預覽
      try {
        const downloadURL = await getFileDownloadURL(path);
        setCoverUrlDisplay(downloadURL);
      } catch (previewError) {
        console.warn("無法獲取預覽 URL，但上傳成功:", previewError);
      }

      // 更新表單值
      setValue("drama_cover_url", path, { shouldDirty: true, shouldValidate: true });
      setCoverUploading(false);
    } catch (err: any) {
      console.error("Error uploading cover image:", err);
      setCoverUploadError(err.message || "上傳失敗");
      setCoverUploading(false);
      setCoverUrlDisplay("");
    }

    // 重置 input
    if (coverFileInputRef.current) {
      coverFileInputRef.current.value = "";
    }
  };

  const handleCoverImageClick = () => {
    coverFileInputRef.current?.click();
  };

  // 影集管理
  const createVideoMutation = useMutation({
    mutationFn: async (videoData: Omit<VideoMeta, "id" | "createdAt" | "updatedAt">) => {
      const videoId = await createVideo(videoData);
      if (id && !isNew) {
        await addEpisodeToDrama(id, videoId);
      }
      return videoId;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drama", id, "with-episodes"] });
      queryClient.invalidateQueries({ queryKey: ["drama", id] });
      setShowVideoForm(false);
      setEditingVideo(null);
    },
  });

  const updateVideoMutation = useMutation({
    mutationFn: async ({
      videoId,
      data,
    }: {
      videoId: string;
      data: Partial<Omit<VideoMeta, "id" | "createdAt" | "updatedAt">>;
    }) => {
      await updateVideo(videoId, data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drama", id, "with-episodes"] });
      setShowVideoForm(false);
      setEditingVideo(null);
    },
  });

  const deleteVideoMutation = useMutation({
    mutationFn: async (videoId: string) => {
      if (id && !isNew) {
        await removeEpisodeFromDrama(id, videoId, true); // 同時刪除 video 文檔
      } else {
        await deleteVideo(videoId);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drama", id, "with-episodes"] });
      queryClient.invalidateQueries({ queryKey: ["drama", id] });
    },
  });

  const handleNewVideo = () => {
    setEditingVideo(null);
    setShowVideoForm(true);
  };

  const handleExportCSV = useCallback(() => {
    if (videos.length === 0) return;
    const csvData = videos.map((video) => {
      const order = video.order ?? 0;
      const prevVideo = videos.find((v) => (v.order ?? 0) === order - 1);
      const prevPlayTimes =
        prevVideo?.play_times != null && Number.isFinite(prevVideo.play_times)
          ? prevVideo.play_times
          : 0;
      const paidCount = episodeStats?.episodeUnlockCounts[order] ?? 0;
      const conversionRate =
        order <= 1 || prevPlayTimes === 0
          ? ""
          : `${((paidCount / prevPlayTimes) * 100).toFixed(1)}%`;
      return {
        集數: order,
        標題: video.caption || video.name || "未命名",
        影片ID: video.vid,
        時長秒:
          typeof video.duration === "number" && Number.isFinite(video.duration) ? video.duration : 0,
        喜愛數: realVideoLike(video),
        播放次數: video.play_times || 0,
        付費人數: paidCount,
        轉換率: conversionRate,
        上線狀態:
          video.release_status === "released"
            ? "已上線"
            : video.release_status === "scheduled"
              ? "已排程"
              : "未排程",
        付費狀態: video.vip ? "付費" : "免費",
      };
    });
    const headers = [
      { key: "集數" as const, label: "集數" },
      { key: "標題" as const, label: "標題" },
      { key: "影片ID" as const, label: "影片 ID" },
      { key: "時長秒" as const, label: "時長（秒）" },
      { key: "喜愛數" as const, label: "喜愛數" },
      { key: "播放次數" as const, label: "播放次數" },
      { key: "付費人數" as const, label: "付費人數" },
      { key: "轉換率" as const, label: "轉換率（本集付費/上集觀看）" },
      { key: "上線狀態" as const, label: "上線狀態" },
      { key: "付費狀態" as const, label: "付費狀態" },
    ];
    const title = drama?.drama_title || id || "影集";
    const now = new Date();
    const ts = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, "0")}${String(now.getDate()).padStart(2, "0")}`;
    exportToCSV(csvData, headers, `${title}_影集數據_${ts}`);
  }, [videos, drama, id, episodeStats]);

  const handleEditVideo = (video: VideoMeta & { id: string }) => {
    setEditingVideo(video);
    setShowVideoForm(true);
  };

  const handleDeleteVideo = (videoId: string) => {
    if (confirm("確定要刪除此影集嗎？")) {
      deleteVideoMutation.mutate(videoId);
    }
  };

  const handleCancelOrBack = () => {
    if (isDirty) {
      // 有修改時，確認是否要取消
      if (confirm("確定要取消編輯嗎？未儲存的變更將會遺失。")) {
        // 重置表單到原始狀態
        if (drama) {
          const resetData: DramaFormData = {
            drama_id: drama.drama_id || "",
            drama_title: drama.drama_title || "",
            drama_title_en: drama.drama_title_en || "",
            isReleased: drama.isReleased || false,
            new_release: drama.new_release || false,
            ad_unlock_enabled: drama.ad_unlock_enabled || false,
            coin_unlock_enabled: drama.coin_unlock_enabled !== false,
            drama_video_orientation: drama.drama_video_orientation ?? 0,
            drama_cover_url: drama.drama_cover_url || "",
            search_keywords: drama.search_keywords || [],
            tags: drama.tags || [],
            company: drama.company || "",
            actors: drama.actors || [],
            related_products: drama.related_products || [],
          };
          reset(resetData);
          setCoverUrlDisplay(getDramaCoverUrl(drama.drama_cover_url));
        }
      }
    } else {
      // 沒有修改時,直接返回詳情頁(新增則回列表)
      navigate(isNew ? "/dramas" : `/dramas/${id}`);
    }
  };

  const handleSaveVideo = async (data: Omit<VideoMeta, "id" | "createdAt" | "updatedAt">) => {
    if (editingVideo) {
      await updateVideoMutation.mutateAsync({
        videoId: editingVideo.id,
        data,
      });
    } else {
      await createVideoMutation.mutateAsync(data);
    }
  };

  // 資料合理性檢查（上架前）
  const validateDramaForRelease = (): { isValid: boolean; errors: string[] } => {
    const errors: string[] = [];
    const formData = watch();

    // 基本必填欄位檢查
    if (!formData.drama_id || formData.drama_id.trim() === "") {
      errors.push("劇集 ID 為必填");
    }

    if (!formData.drama_title || formData.drama_title.trim() === "") {
      errors.push("劇集標題為必填");
    }

    // 封面圖片檢查（同時檢查表單值和顯示值）
    const formCoverUrl = formData.drama_cover_url || "";
    const displayCoverUrl = coverUrlDisplay || "";
    const coverUrl = displayCoverUrl || formCoverUrl;
    if (!coverUrl || coverUrl.trim() === "") {
      errors.push("封面圖片為必填");
    }

    // 影集檢查（僅在非新劇集時檢查）
    if (!isNew && videos.length === 0) {
      errors.push("至少要有 1 個影集");
    }

    // 全劇買斷金幣價設定檢查（讀取劇集文件，不再是 form 欄位）
    if (drama?.enable_full_drama_coin_purchase) {
      if (!drama.full_drama_coin_cost || drama.full_drama_coin_cost <= 0) {
        errors.push("啟用全劇一次買斷時，全劇金幣價格必須大於 0（請至定價頁調整）");
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
    };
  };

  // 上下架 mutation
  const releaseMutation = useMutation({
    mutationFn: async (isReleased: boolean) => {
      if (!id || isNew) {
        throw new Error("無法對新劇集進行上下架操作，請先儲存");
      }
      // 手動上架時一併取消預約（後端遇到已上架的劇也只會清掉排程）
      await updateDrama(id, isReleased && drama?.scheduled_publish_at
        ? { isReleased, scheduled_publish_at: null }
        : { isReleased });
      return isReleased;
    },
    onSuccess: (isReleased) => {
      queryClient.invalidateQueries({ queryKey: ["drama", id, "with-episodes"] });
      queryClient.invalidateQueries({ queryKey: ["drama", id] });
      queryClient.invalidateQueries({ queryKey: ["dramas"] });
      setValue("isReleased", isReleased);
      setReleaseConfirm({ isOpen: false, action: "release" });
      setReleaseError(null);
    },
    onError: (error) => {
      console.error("上下架操作失敗:", error);
      const errorMessage = error instanceof Error ? error.message : "上下架操作失敗，請稍後再試";
      setReleaseError(errorMessage);
      setReleaseConfirm({ isOpen: false, action: "release" });
    },
  });

  // 預約上架 mutation（at = null 取消預約）
  const scheduleMutation = useMutation({
    mutationFn: async (at: Date | null) => {
      if (!id || isNew) throw new Error("請先儲存劇集後再預約上架");
      await updateDrama(id, { scheduled_publish_at: at });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["drama", id, "with-episodes"] });
      queryClient.invalidateQueries({ queryKey: ["drama", id] });
      queryClient.invalidateQueries({ queryKey: ["dramas"] });
      setScheduleOpen(false);
      setScheduleError(null);
    },
    onError: (error) => {
      setScheduleError(error instanceof Error ? error.message : "預約上架儲存失敗，請稍後再試");
    },
  });

  const handleScheduleSave = (at: Date) => {
    // 與上架相同的資料檢查；集數本身的排程由後端處理（到點時沒有已上線集數會延到下個整點）
    const validation = validateDramaForRelease();
    if (!validation.isValid) {
      setScheduleOpen(false);
      setValidationErrors(validation.errors);
      setShowValidationErrors(true);
      return;
    }
    scheduleMutation.mutate(at);
  };

  // 處理上下架按鈕點擊
  const handleReleaseClick = () => {
    const currentIsReleased = watch("isReleased");
    if (isNew) {
      alert("請先儲存劇集後才能進行上下架操作");
      return;
    }

    if (currentIsReleased) {
      // 下架：直接顯示確認視窗
      setReleaseConfirm({ isOpen: true, action: "unrelease" });
    } else {
      // 上架：先進行資料檢查
      const validation = validateDramaForRelease();
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        setShowValidationErrors(true);
        return;
      }
      // 檢查通過，顯示確認視窗
      setReleaseConfirm({ isOpen: true, action: "release" });
    }
  };

  // 確認上下架操作
  const confirmRelease = () => {
    if (releaseConfirm.action === "release") {
      // 上架：再次檢查（以防資料在確認期間被修改）
      const validation = validateDramaForRelease();
      if (!validation.isValid) {
        setValidationErrors(validation.errors);
        setShowValidationErrors(true);
        setReleaseConfirm({ isOpen: false, action: "release" });
        return;
      }
      releaseMutation.mutate(true);
    } else {
      // 下架：直接執行
      releaseMutation.mutate(false);
    }
  };

  if ((!isNew && isLoading) || referencesLoading) {
    return (
      <AdminPage>
        <AdminLoadingSkeleton variant="mixed" />
      </AdminPage>
    );
  }

  const currentIsReleased = watch("isReleased");
  const isReleaseButtonDisabled = isNew || releaseMutation.isPending;

  return (
    <AdminPage>
      {/* 錯誤訊息 */}
      {releaseError && (
        <AdminErrorAlert message={releaseError} onDismiss={() => setReleaseError(null)} />
      )}
      {saveError && <AdminErrorAlert message={saveError} onDismiss={() => setSaveError(null)} />}

      <AdminPageHeader
        title={isNew ? "新增劇集" : (drama?.drama_title || "編輯劇集")}
        subtitle={!isNew && id ? `ID: ${id}` : undefined}
        actions={
          <>
            <Button variant="outline" onClick={handleCancelOrBack}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              {isDirty ? "取消" : "返回"}
            </Button>
            {!isNew && !currentIsReleased && (
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  setScheduleError(null);
                  setScheduleOpen(true);
                }}
                disabled={scheduleMutation.isPending}
              >
                <CalendarClock className="mr-2 h-4 w-4" />
                {drama?.scheduled_publish_at
                  ? `預約 ${formatTaipeiHour(drama.scheduled_publish_at)} 上架`
                  : "預約上架"}
              </Button>
            )}
            {!isNew && (
              <Button
                variant={currentIsReleased ? "destructive" : "default"}
                onClick={handleReleaseClick}
                disabled={isReleaseButtonDisabled}
              >
                {releaseMutation.isPending ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    處理中...
                  </>
                ) : currentIsReleased ? (
                  <>
                    <Ban className="mr-2 h-4 w-4" />
                    下架
                  </>
                ) : (
                  <>
                    <Upload className="mr-2 h-4 w-4" />
                    上架
                  </>
                )}
              </Button>
            )}
            <Button
              type="submit"
              form="drama-edit-form"
              disabled={mutation.isPending || !isDirty}
            >
              {mutation.isPending ? (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              ) : (
                <Save className="mr-2 h-4 w-4" />
              )}
              {mutation.isPending ? "儲存中..." : "儲存"}
            </Button>
          </>
        }
      />

      <form id="drama-edit-form" onSubmit={handleSubmit(onSubmit, (invalid) => {
        const labels: Record<string, string> = {
          drama_id: "劇集 ID", drama_title: "劇集標題", drama_title_en: "英文標題",
          isReleased: "上架狀態", new_release: "新上架", ad_unlock_enabled: "廣告解鎖",
          coin_unlock_enabled: "金幣解鎖", drama_video_orientation: "影片方向",
          drama_cover_url: "封面圖片", search_keywords: "搜尋關鍵字", tags: "標籤",
          company: "製片公司", actors: "演員（卡司）", related_products: "相關商品",
        };
        setSaveError(`尚未儲存，請檢查：${Object.keys(invalid).map((key) => labels[key] || key).join("、")}。`);
      })} className="space-y-6">
        {/* M6: 草稿還原橫幅 */}
        {draftRecovery && (
          <Card className="border-amber-300 dark:border-amber-700 bg-amber-50 dark:bg-amber-900/20">
            <CardContent className="p-4 flex items-start gap-3">
              <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
              <div className="flex-1 text-sm">
                <div className="font-medium text-amber-900 dark:text-amber-100 mb-1">
                  發現未提交的草稿
                </div>
                <div className="text-amber-800 dark:text-amber-200">
                  {(() => {
                    const minutes = Math.max(
                      1,
                      Math.round((Date.now() - draftRecovery.savedAt) / 60000)
                    );
                    if (minutes < 60) return `${minutes} 分鐘前自動保存於本機。`;
                    const hours = Math.round(minutes / 60);
                    if (hours < 24) return `${hours} 小時前自動保存於本機。`;
                    const days = Math.round(hours / 24);
                    return `${days} 天前自動保存於本機。`;
                  })()}{" "}
                  是否還原？
                </div>
              </div>
              <div className="flex gap-2 shrink-0">
                <Button type="button" size="sm" onClick={handleRestoreDraft}>
                  還原
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={handleDiscardDraft}
                >
                  忽略
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* 基本資訊與封面圖片 */}
        <Card>
          <CardHeader>
            <CardTitle>基本資訊</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* 左側：封面圖 (1/3) */}
              <div className="md:col-span-1">
                <Label className="mb-2 block">
                  封面圖片 <span className="text-destructive">*</span>
                </Label>
                <div className="relative">
                  <input
                    ref={coverFileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleCoverImageSelect}
                    disabled={coverUploading}
                    className="hidden"
                    id="cover-image-input"
                  />
                  <div
                    onClick={handleCoverImageClick}
                    className={`relative aspect-[2/3] w-full border-2 border-dashed rounded-lg overflow-hidden cursor-pointer transition-colors ${
                      coverUploading
                        ? "border-muted cursor-not-allowed"
                        : "border-muted hover:border-primary"
                    } bg-muted/30`}
                  >
                    {coverUrlDisplay ? (
                      <>
                        <img
                          src={coverUrlDisplay}
                          alt="封面預覽"
                          className="w-full h-full object-cover"
                        />
                        {!coverUploading && (
                          <div className="absolute inset-0 bg-black bg-opacity-0 hover:bg-opacity-30 transition-all flex items-center justify-center">
                            <div className="opacity-0 hover:opacity-100 transition-opacity text-white text-sm font-medium">
                              <ImageIcon className="h-6 w-6 mx-auto mb-1" />
                              點擊更換
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="w-full h-full flex flex-col items-center justify-center text-muted-foreground">
                        <ImageIcon className="h-12 w-12 mb-2" />
                        <span className="text-sm">點擊上傳封面</span>
                      </div>
                    )}
                    {coverUploading && (
                      <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
                        <Loader2 className="h-8 w-8 animate-spin text-white" />
                      </div>
                    )}
                  </div>
                  {(errors.drama_cover_url?.message || coverUploadError) && (
                    <p className="mt-2 text-sm text-destructive">
                      {errors.drama_cover_url?.message || coverUploadError}
                    </p>
                  )}
                  {!coverUploading && coverUrlDisplay && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="mt-2 w-full text-destructive hover:text-destructive"
                      onClick={(e) => {
                        e.stopPropagation();
                        setCoverUrlDisplay("");
                        setValue("drama_cover_url", "", { shouldDirty: true, shouldValidate: true });
                      }}
                    >
                      <Trash2 className="mr-2 h-4 w-4" />
                      移除圖片
                    </Button>
                  )}
                </div>
              </div>

              {/* 右側：基本資料 (2/3) */}
              <div className="md:col-span-2 space-y-4">
                {/* 劇集 ID：新增時系統自動產生，不顯示；編輯時唯讀顯示供查找 */}
                {!isNew && (
                  <div className="space-y-2">
                    <Label>劇集 ID</Label>
                    <div className="flex h-10 items-center rounded-md border border-input bg-muted/30 px-3 text-sm font-mono text-muted-foreground">
                      {watch("drama_id") || "—"}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <Label htmlFor="drama_title">
                    劇集標題 <span className="text-destructive">*</span>
                  </Label>
                  <Input
                    id="drama_title"
                    {...register("drama_title")}
                    className={errors.drama_title ? "border-destructive" : ""}
                  />
                  {errors.drama_title && (
                    <p className="text-sm text-destructive">{errors.drama_title.message}</p>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="drama_title_en">英文標題</Label>
                  <Input
                    id="drama_title_en"
                    {...register("drama_title_en")}
                  />
                </div>

                <div className="space-y-2">
                  <Label>搜尋關鍵字</Label>
                  <div className="flex gap-2">
                    <Input
                      type="text"
                      value={keywordInput}
                      onChange={(e) => setKeywordInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === "Enter") {
                          e.preventDefault();
                          handleAddKeyword();
                        }
                      }}
                      placeholder="輸入關鍵字後按 Enter"
                      className="flex-1"
                    />
                    <Button type="button" onClick={handleAddKeyword}>
                      <Plus className="mr-2 h-4 w-4" />
                      新增
                    </Button>
                  </div>
                  <div className="flex flex-wrap gap-2 mt-2">
                    {(watch("search_keywords") || []).map((keyword, index) => (
                      <Badge key={index} variant="secondary" className="gap-1">
                        {keyword}
                        <button
                          type="button"
                          onClick={() => handleRemoveKeyword(index)}
                          className="hover:bg-muted rounded-full p-0.5"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Dashboard - 劇集表現數據 */}
        {!isNew && id && (
          <Card>
            <CardHeader>
              <CardTitle>劇集表現數據</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                <AdminStatsCard
                  icon={<Eye className="h-5 w-5" />}
                  label="總觀看次數"
                  value={realPlayTimes.toLocaleString()}
                />
                <AdminStatsCard
                  icon={<Bookmark className="h-5 w-5" />}
                  label="總收藏數"
                  value={favoritesLoading ? "..." : realFavoritesCount.toLocaleString()}
                />
                <AdminStatsCard
                  icon={<Heart className="h-5 w-5" />}
                  label="總喜愛數"
                  value={likesLoading ? "..." : realTotalLikes.toLocaleString()}
                />
                <AdminStatsCard
                  icon={<Coins className="h-5 w-5" />}
                  label="總解鎖收益（金幣）"
                  value={episodeStatsLoading ? "..." : (episodeStats?.totalUnlockRevenue ?? 0).toLocaleString()}
                />
              </div>
            </CardContent>
          </Card>
        )}

        {!isNew && id && drama && (
          <DisplayMetricsCard
            dramaDocId={id}
            drama={drama}
            favoritesCount={favoritesCount}
            videos={videos}
            episodeDisplayByVid={episodeDisplayByVid}
          />
        )}

        {/* 狀態設定 */}
        <Card>
          <CardHeader>
            <CardTitle>狀態設定</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center gap-3">
              <Switch
                id="new_release"
                checked={watch("new_release")}
                onCheckedChange={(checked) => setValue("new_release", checked, { shouldDirty: true })}
              />
              <Label htmlFor="new_release">新上架</Label>
            </div>

            <div className="flex items-start gap-3">
              <Switch
                id="coin_unlock_enabled"
                checked={watch("coin_unlock_enabled")}
                onCheckedChange={(checked) => setValue("coin_unlock_enabled", checked, { shouldDirty: true })}
              />
              <div className="space-y-1">
                <Label htmlFor="coin_unlock_enabled">金幣解鎖</Label>
                <p className="text-xs text-muted-foreground">
                  關閉後，此劇不能用金幣解鎖（單集與全劇皆是），只保留廣告解鎖。
                  ⚠️ 關閉時請確認「廣告解鎖」是開的，否則付費集數會變成無法解鎖。
                  舊版 App 仍會顯示金幣按鈕，但後端會擋下扣幣。
                </p>
              </div>
            </div>

            <div className="flex items-start gap-3">
              <Switch
                id="ad_unlock_enabled"
                checked={watch("ad_unlock_enabled")}
                onCheckedChange={(checked) => setValue("ad_unlock_enabled", checked, { shouldDirty: true })}
              />
              <div className="space-y-1">
                <Label htmlFor="ad_unlock_enabled">廣告解鎖</Label>
                <p className="text-xs text-muted-foreground">
                  啟用後，此劇付費集數預設跳 Rewarded Ad（解鎖 24 小時）。
                  用戶可關閉廣告改用金幣全劇解鎖。免費集數沿用 VIP 設定不變。
                </p>
              </div>
            </div>

            {/* 影片方向：2.5.0 起支援橫式。建劇時決定；已有影集後鎖定
                （方向驅動 App 的播放 scaling 與卡片比例，中途改會讓既有集顯示異常）。 */}
            <Separator />
            <div className="space-y-2">
              <Label htmlFor="drama_video_orientation">影片方向</Label>
              {/* 鎖定時不能用 disabled（RHF 會讓值變 undefined 洗掉欄位），
                  改為鎖住 option：只留當前值可選，行為等同唯讀。 */}
              <select
                id="drama_video_orientation"
                {...register("drama_video_orientation", { valueAsNumber: true })}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                {/* 鎖定依據用 Firestore 已存值（不用 watch()）：表單值可能被草稿等未存檔來源改動，
                    若跟著表單值鎖會讓使用者鎖在錯的方向上。 */}
                <option value={0} disabled={!isNew && videos.length > 0 && (drama?.drama_video_orientation ?? 0) !== 0}>
                  直式（9:16）
                </option>
                <option value={1} disabled={!isNew && videos.length > 0 && (drama?.drama_video_orientation ?? 0) !== 1}>
                  橫式（16:9）
                </option>
              </select>
              {!isNew && videos.length > 0 ? (
                <p className="text-xs text-muted-foreground">
                  已有 {videos.length} 部影集，方向鎖定。如需變更請先移除所有影集。
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  決定 App 的播放縮放與卡片/封面比例。新增第一部影集後即鎖定。
                </p>
              )}
            </div>
          </CardContent>
        </Card>

        {/* 參考欄位 */}
        <Card>
          <CardHeader>
            <CardTitle>參考欄位</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <MultiSelect
              options={tagOptions}
              value={watchTags}
              onChange={(value) => setValue("tags", value, { shouldDirty: true })}
              label="標籤"
              placeholder="選擇標籤..."
            />

            <div className="space-y-2">
              <Label htmlFor="company">製片公司</Label>
              <select
                id="company"
                {...register("company")}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
              >
                <option value="">請選擇公司</option>
                {companyOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <ArtistCastPicker
              options={actorOptions}
              value={watchActors}
              onChange={(value) => setValue("actors", value, { shouldDirty: true })}
              statusById={artistHubEnabled ? artistStatusById : undefined}
            />

            <MultiSelect
              options={productOptions}
              value={watchRelatedProducts}
              onChange={(value) => setValue("related_products", value, { shouldDirty: true })}
              label="相關商品"
              placeholder="選擇商品..."
            />
            <div className="border-t pt-4 space-y-3">
              {saveError && <AdminErrorAlert message={saveError} />}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground" role="status">
                  {isDirty ? "有尚未儲存的變更" : "目前沒有未儲存的變更"}；公司與卡司順序需按儲存才會生效。
                </p>
                <Button type="submit" disabled={mutation.isPending || !isDirty}>
                  {mutation.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
                  {mutation.isPending ? "儲存中..." : "儲存劇集變更"}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* 定價設定入口（已移至獨立頁面） */}
        <Card>
          <CardHeader>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div>
                <CardTitle>定價設定</CardTitle>
                <CardDescription>
                  單集金幣、全劇一次買斷、免費集數等定價相關設定已獨立到專屬頁面，包含試算與確認流程。
                </CardDescription>
              </div>
              {!isNew && id ? (
                <Button asChild variant="outline">
                  <Link to={`/dramas/${id}/pricing`}>前往定價頁</Link>
                </Button>
              ) : (
                <Button variant="outline" disabled>
                  前往定價頁
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {isNew ? (
              <p className="text-sm text-muted-foreground">
                新劇集請先儲存基本資訊後，再從定價頁進行價格設定。
              </p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground">單集解鎖金幣</div>
                  <div className="font-medium tabular-nums">
                    {drama?.episode_unlock_coin_cost &&
                    drama.episode_unlock_coin_cost > 0
                      ? `${drama.episode_unlock_coin_cost} 金幣`
                      : "30 金幣（預設）"}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground">全劇一次買斷</div>
                  <div className="font-medium tabular-nums">
                    {drama?.enable_full_drama_coin_purchase
                      ? `${(drama.full_drama_coin_cost || 0).toLocaleString()} 金幣`
                      : "未啟用"}
                  </div>
                </div>
                <div className="space-y-1">
                  <div className="text-xs text-muted-foreground">免費集數</div>
                  <div className="font-medium tabular-nums">
                    前 {currentFreeEpisodeCount} 集
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* 影片上傳：CMS 端直傳功能暫時停用（2026-04-29）。
            原因：functions getBytePlusUploadToken 回傳的 stsToken 為空字串，
            BytePlus uploader SDK 拒絕（"The format of stsToken is incorrect"）。
            根因：functions/byteplus.ts:201-221 把 ApplyUploadInfo 的 UploadAddress 當成 STS Token，
            但 ApplyUploadInfo 不會回 SessionToken / AccessKeyId，要另外呼叫 BytePlus IAM AssumeRole。
            目前流程：admin 在 BytePlus 後台直傳 → 取 vid → 「新增影集」貼 vid 並用「從 BytePlus 帶入」自動填欄位。 */}
        {false && !isNew && id && drama && (
          <Card>
            <CardContent className="p-6">
              <VideoUploadSection
                dramaId={drama?.drama_id ?? ""}
                existingVideos={videos}
                onUploadComplete={() => {
                  queryClient.invalidateQueries({ queryKey: ["drama", id, "with-episodes"] });
                }}
              />
            </CardContent>
          </Card>
        )}

        {/* 影集管理 */}
        {!isNew && id && (
          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle>影集管理</CardTitle>
                  <div className="mt-2 text-sm text-muted-foreground">
                    <div className="font-medium mb-1">上架流程：</div>
                    <ol className="list-decimal list-outside ml-5 space-y-0.5">
                      <li>
                        在{" "}
                        <a
                          href="https://console.byteplus.com/vod/"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="underline"
                        >
                          BytePlus VOD 後台
                        </a>{" "}
                        上傳影片並複製 vid
                        <div className="mt-0.5 text-xs text-amber-600 dark:text-amber-400">
                          ⚠️ 上傳時記得選擇 workflow 進行轉檔（多碼率 / HLS），否則 App 端可能無法正常播放
                        </div>
                        <div className="mt-0.5 text-xs text-amber-600 dark:text-amber-400">
                          ⚠️ 橫式劇：片源請用 16:9（如 1920×1080），但封面仍請上傳 2:3 直式主視覺（App 卡片一律直式框）；
                          「影片方向」須在新增第一部影集前設定正確（之後鎖定）
                        </div>
                      </li>
                      <li>點「新增影集」貼上 vid</li>
                      <li>點對話框內「從 BytePlus 帶入」自動填寬高 / 時長</li>
                      <li>填集數與付費設定後保存</li>
                    </ol>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleExportCSV}
                    disabled={videos.length === 0}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    匯出 CSV
                  </Button>
                  <Button type="button" onClick={handleNewVideo}>
                    <Plus className="mr-2 h-4 w-4" />
                    新增影集
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              {videos.length > 0 ? (
                <div className="rounded-md border overflow-hidden">
                  <table className="w-full">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          順序
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          集數
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          標題
                        </th>
                        <th className="px-3 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          ID
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          時長（秒）
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          喜愛數
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          播放次數
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          付費人數
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          轉換率
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          上線時間設定
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          付費
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          操作
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {videos.map((video) => (
                        <tr
                          key={video.id}
                          className="hover:bg-muted/50 transition-colors"
                        >
                          <td className="px-4 py-4 whitespace-nowrap text-sm text-muted-foreground tabular-nums">
                            {video.order}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                            {(video.vid && episodeDisplayByVid[video.vid]) ||
                              `第 ${video.order} 集`}
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-foreground">
                              {video.caption || video.name || "未命名"}
                            </div>
                          </td>
                          <td className="px-3 py-4 whitespace-nowrap text-center">
                            <button
                              type="button"
                              title={video.vid}
                              onClick={() => {
                                navigator.clipboard.writeText(video.vid);
                                setCopiedVid(video.vid);
                                setTimeout(() => setCopiedVid(null), 2000);
                              }}
                              className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded hover:bg-muted transition-colors text-muted-foreground"
                            >
                              {copiedVid === video.vid ? (
                                <>
                                  <Check className="h-4 w-4 text-green-500" />
                                  已複製
                                </>
                              ) : (
                                <>
                                  <Copy className="h-4 w-4" />
                                  複製
                                </>
                              )}
                            </button>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                            {typeof video.duration === "number" && Number.isFinite(video.duration) ? video.duration : "—"}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Heart className="h-4 w-4 text-pink-500" />
                              <span className="font-medium">{realVideoLike(video).toLocaleString()}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Play className="h-4 w-4 text-blue-500" />
                              <span className="font-medium">{(video.play_times || 0).toLocaleString()}</span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center gap-1 text-sm text-muted-foreground">
                              <Coins className="h-4 w-4 text-amber-500" />
                              <span className="font-medium">
                                {(episodeStats?.episodeUnlockCounts[video.order ?? 0] ?? 0).toLocaleString()}
                              </span>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-muted-foreground">
                            {(() => {
                              const order = video.order ?? 0;
                              const prevVideo = videos.find((v) => (v.order ?? 0) === order - 1);
                              const prevPlayTimes =
                                prevVideo?.play_times != null && Number.isFinite(prevVideo.play_times)
                                  ? prevVideo.play_times
                                  : 0;
                              const paidCount = episodeStats?.episodeUnlockCounts[order] ?? 0;
                              if (order <= 1 || prevPlayTimes === 0) return "—";
                              const rate = (paidCount / prevPlayTimes) * 100;
                              return `${rate.toFixed(1)}%`;
                            })()}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex flex-col gap-1">
                              <div className="text-sm text-muted-foreground">
                                {video.scheduled_release_time_display
                                  ? video.scheduled_release_time_display
                                  : video.scheduled_release_time
                                  ? new Date(video.scheduled_release_time).toLocaleString("zh-TW")
                                  : "—"}
                              </div>
                              {/* 上傳狀態徽章（僅在非 completed 時顯示，提醒 admin 影片仍在處理中） */}
                              {video.upload_status && video.upload_status !== "completed" && (
                                <div title={video.upload_error || undefined}>
                                  {video.upload_status === "pending" && (
                                    <AdminStatusBadge label="⏳ 等待上傳" tone="neutral" />
                                  )}
                                  {video.upload_status === "uploading" && (
                                    <AdminStatusBadge label="⬆️ 上傳中" tone="info" />
                                  )}
                                  {video.upload_status === "processing" && (
                                    <AdminStatusBadge label="⚙️ 處理中" tone="info" />
                                  )}
                                  {video.upload_status === "failed" && (
                                    <AdminStatusBadge label="❌ 上傳失敗" tone="danger" />
                                  )}
                                </div>
                              )}
                              {video.release_status && (
                                <div>
                                  {video.release_status === "scheduled" && (
                                    <AdminStatusBadge label="已排程" tone="info" />
                                  )}
                                  {video.release_status === "released" && (
                                    <AdminStatusBadge label="已上線" tone="success" />
                                  )}
                                  {video.release_status === "not_scheduled" && (
                                    <AdminStatusBadge label="未排程" tone="neutral" />
                                  )}
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {video.vip ? (
                              <AdminStatusBadge label="🔒 付費" tone="warning" />
                            ) : (
                              <AdminStatusBadge label="免費" tone="success" />
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm">
                            <ActionMenu
                              actions={[
                                {
                                  label: "預覽",
                                  onClick: () => setPreviewVideo(video),
                                },
                                {
                                  label: "編輯",
                                  onClick: () => handleEditVideo(video),
                                },
                                {
                                  label: "刪除",
                                  onClick: () => handleDeleteVideo(video.id),
                                  disabled: deleteVideoMutation.isPending,
                                  variant: "danger",
                                },
                              ]}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="rounded-lg border border-dashed p-12 text-center">
                  <p className="text-muted-foreground mb-4">尚無影集資料</p>
                  <Button type="button" onClick={handleNewVideo}>
                    <Plus className="mr-2 h-4 w-4" />
                    新增第一個影集
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        )}

      </form>

      {/* 影集表單對話框 */}
      {!isNew && id && (
        <VideoFormDialog
          isOpen={showVideoForm}
          onClose={() => {
            setShowVideoForm(false);
            setEditingVideo(null);
          }}
          onSave={handleSaveVideo}
          video={editingVideo || undefined}
          dramaId={drama?.drama_id || ""}
          defaultOrder={videos.length > 0 ? Math.max(...videos.map((v) => v.order || 0)) + 1 : 1}
          dramaOrientation={drama?.drama_video_orientation ?? 0}
          dramaEpisodeUnlockCost={drama?.episode_unlock_coin_cost}
        />
      )}

      {/* M4: 集數預覽 Dialog */}
      <VideoPreviewDialog
        isOpen={previewVideo !== null}
        onClose={() => setPreviewVideo(null)}
        video={previewVideo}
        dramaId={drama?.drama_id || id || ""}
        dramaTitle={drama?.drama_title}
      />


      {/* 預約上架 */}
      <PublishScheduleDialog
        isOpen={scheduleOpen}
        current={drama?.scheduled_publish_at ?? null}
        pending={scheduleMutation.isPending}
        error={scheduleError}
        onSave={handleScheduleSave}
        onCancelSchedule={() => scheduleMutation.mutate(null)}
        onClose={() => setScheduleOpen(false)}
      />

      {/* 上下架確認視窗 */}
      <ConfirmDialog
        isOpen={releaseConfirm.isOpen}
        title={releaseConfirm.action === "release" ? "確認上架" : "確認下架"}
        message={
          releaseConfirm.action === "release"
            ? "確定要上架此劇集嗎？上架前已進行資料檢查。"
            : "確定要下架此劇集嗎？"
        }
        extra={
          releaseConfirm.action === "release" && drama ? (
            <div className="mt-2 rounded-md border bg-muted/30 p-3 text-sm space-y-1.5">
              <div className="flex items-center gap-1.5 font-medium">
                <Coins className="h-3.5 w-3.5" />
                分潤費率
              </div>
              {drama.paid_coin_rate != null && drama.paid_coin_rate > 0 ? (
                <div className="text-foreground">
                  此劇 paid_coin_rate ={" "}
                  <span className="font-mono font-semibold">
                    {drama.paid_coin_rate.toFixed(3)}
                  </span>{" "}
                  NTD/coin
                </div>
              ) : (
                <div className="flex items-start gap-1.5 text-amber-600 dark:text-amber-400">
                  <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
                  <span>
                    此劇尚未設定 paid_coin_rate。上架後此劇所有付費收入將計算為{" "}
                    <span className="font-semibold">分潤金額 0</span>。
                    若要正確分潤，請至{" "}
                    <Link
                      to="/financial/revenue-configs"
                      className="underline font-medium"
                    >
                      財務 → 分潤費率設定
                    </Link>{" "}
                    補上費率。
                  </span>
                </div>
              )}
            </div>
          ) : undefined
        }
        confirmText={releaseConfirm.action === "release" ? "上架" : "下架"}
        cancelText="取消"
        variant={releaseConfirm.action === "release" ? "default" : "danger"}
        onConfirm={confirmRelease}
        onCancel={() => setReleaseConfirm({ isOpen: false, action: "release" })}
      />

      {/* 資料檢查錯誤視窗 */}
      {showValidationErrors && validationErrors.length > 0 && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowValidationErrors(false);
              setValidationErrors([]);
            }
          }}
        >
          <Card className="max-w-md w-full mx-4 shadow-xl">
            <CardHeader>
              <CardTitle>資料檢查失敗</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-muted-foreground mb-6">
                <p className="mb-3">以下資料不符合上架要求：</p>
                <ul className="list-disc list-inside space-y-1">
                  {validationErrors.map((error, index) => (
                    <li key={index}>{error}</li>
                  ))}
                </ul>
              </div>
              <div className="flex gap-3 justify-end">
                <Button
                  variant="destructive"
                  onClick={() => {
                    setShowValidationErrors(false);
                    setValidationErrors([]);
                  }}
                >
                  確定
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </AdminPage>
  );
}
